import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  ENDPOINTS,
  PRIVATE_API,
  PUBLIC_API,
  PAYWALL_URL,
  PRIVATE_ENDPOINTS,
  PUBLIC_ENDPOINTS,
} from '@/constants/constants';
import { useAuth } from '@/hooks/useAuth';

import type { article } from '@/types/articles';
import type { category } from '@/types/category';
import type { ContentLabel } from '@/types/models.types';
import type { CMSContextType, ContentResponse } from '@/types/util.types';
import { sortCategoriesWithOrder } from '@/utils/sortCategoriesOrder';

export const CMSContext = createContext<CMSContextType | null>(null);
export const useCms = () => useContext(CMSContext);

/** This hook contains all Strapi CMS related functionality and handles all
 * requests to the Strapi API.
 */
export const ContentProvider = ({ children }: { children: React.ReactNode }) => {
  const { getAccessTokenOrRefresh, msalStatus } = useAuth();
  const { inProgress } = useMsal();
  const [latestArticle, setLatestArticle] = useState<article>();
  const [recentArticles, setRecentArticles] = useState<article[]>([]);
  const [recentArticlesFeaturedCategories, setRecentArticlesFeaturedCategories] = useState<article[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<article[]>([]);
  const [articleDetail, setArticleDetail] = useState<article>();
  const [categories, setCategories] = useState<category[]>([]);
  const [contentLabels, setContentLabels] = useState<ContentLabel[]>([]);
  const [homepageCategories, setHomepageCategories] = useState<category[]>([]);
  const [isLoadingArticle, setIsLoadingArticle] = useState<boolean>(true);

  const fetchData = useCallback(
    async (endpoint: string) => {
      const options: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        if (token) options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

        const baseUrl = token ? PRIVATE_API : PUBLIC_API;
        const url = `${baseUrl}${endpoint}`;
        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Get recent published articles from the CMS
   */
  const getArticlesByRecent = useCallback(async () => {
    try {
      const articles = await fetchData(ENDPOINTS.getArticlesByRecent);
      setRecentArticles(articles);
      setLatestArticle(articles[0]);

      const articlesWithFeaturedCategories = articles.filter((article: article) =>
        article.categories.some((category: category) => category.featured)
      );

      // Keeps the article grid at 12 items
      setRecentArticlesFeaturedCategories(
        articlesWithFeaturedCategories.length < 12 ? articles : articlesWithFeaturedCategories
      );
    } catch (error) {
      console.error('Error getting articles from CMS:', error);
    }
  }, [fetchData]);

  /**
   * Get a specific published articles by its ID
   */
  const getArticleById = useCallback(
    async (id: number, isAuthenticated: boolean = false) => {
      setArticleDetail(undefined);
      setIsLoadingArticle(true);
      let useToken = false;
      let token: string | null = null;

      try {
        if (isAuthenticated) {
          token = await getAccessTokenOrRefresh(import.meta.env.VITE_PAYWALL_API_URL);
          useToken = isAuthenticated && token ? true : false;
        }

        const headers = {
          headers: {
            'Content-Type': 'application/json',
            Authorization: useToken ? `Bearer ${token}` : '',
          },
        };

        const url = useToken ? PRIVATE_ENDPOINTS.getArticleById : PUBLIC_ENDPOINTS.getArticleById;
        const response = await fetch(`${url}/${id}`, headers);
        const data = await response.json();
        setArticleDetail(data);
        setIsLoadingArticle(false);
        return data;
      } catch (error) {
        console.error('Error getting Article by ID:', error);
      }
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Get all featured articles from the CMS
   */
  const getFeaturedArticles = useCallback(async () => {
    try {
      const response = await fetchData(ENDPOINTS.getArticleByFeatured);
      if (response.length === 0) return [];
      const sortedArticles = response.sort(() => Math.random() - 0.5);
      setFeaturedArticles(sortedArticles);
    } catch (error) {
      console.error('Error getting featured articles from CMS:', error);
    }
  }, [fetchData]);

  /**
   * Gets articles for a given category ID and has an optional
   * limit for use in carousels
   */
  const getArticlesByCategory = useCallback(
    async (
      categoryId: number,
      page?: number,
      filters?: { subCategory?: string; contentLabel?: string }
    ): Promise<ContentResponse> => {
      try {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (filters?.subCategory) queryParams.append('subCategory', `${filters.subCategory}`);
        if (filters?.contentLabel) queryParams.append('contentLabel', `${filters.contentLabel}`);

        const url = `${ENDPOINTS.getArticleByCategory}/${categoryId}?${queryParams.toString()}`;
        const response = await fetchData(url);
        return response;
      } catch (error) {
        console.error('Error getting articles by category:', error);
        return { articles: [], pagination: { page: 0, pageSize: 0, pageCount: 0, total: 0 } };
      }
    },
    [fetchData]
  );

  /**
   * Gets all articles by looping through categories
   */
  const getAllArticles = useCallback(async () => {
    try {
      const articles = await Promise.all(
        categories.map(async (category) => {
          const response = await getArticlesByCategory(category.id);
          return response.articles;
        })
      );
      return articles.flat();
    } catch (error) {
      console.error('Error getting all articles:', error);
      return [];
    }
  }, [getArticlesByCategory, categories]);

  /**
   * Gets all articles for a given author ID
   */
  const getArticlesByAuthor = useCallback(
    async (authorId: number, page?: number) => {
      try {
        let endpoint = `${ENDPOINTS.getArticleByAuthor(authorId)}`;

        if (page && page > 1) {
          endpoint += `?page=${page}`;
        }

        const response = await fetchData(endpoint);
        return response;
      } catch (error) {
        console.error('Error getting articles by author:', error);
      }
    },
    [fetchData]
  );

  /**
   * Gets available dashboards for a given author ID
   */
  const getDashboardsByAuthor = useCallback(
    async (authorId: number) => {
      try {
        const url = `${ENDPOINTS.getDashboardsByAuthor(authorId)}`;
        const response = await fetchData(url);
        return response;
      } catch (error) {
        console.error('Error getting dashboards by author:', error);
      }
    },
    [fetchData]
  );

  /**
   * Get all published categories from the CMS
   */
  const getAllCategories = useCallback(async () => {
    try {
      const categories = await fetchData(`${ENDPOINTS.getAllCategories}`);
      const sortedCategories = sortCategoriesWithOrder(categories);
      const featuredCategories = categories.filter((category: category) => category.featured);
      setHomepageCategories(featuredCategories);
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Error getting categories from CMS:', error);
    }
  }, [fetchData]);

  /**
   * Get all content labels from the CMS
   */
  const getAllContentLabels = useCallback(async () => {
    try {
      const response = await fetch(PUBLIC_ENDPOINTS.getAllContentLabels, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setContentLabels(data);
    } catch (error) {
      console.error('Error getting categories from CMS:', error);
    }
  }, [fetchData]);

  /**
   * Get an author by their ID
   */
  const getAuthorById = useCallback(async (id: number) => {
    try {
      const response = await fetch(`${PUBLIC_ENDPOINTS.getAuthorById}/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting author by ID:', error);
    }
  }, []);

  /**
   * Initialize the content by getting all articles, categories and content labels.
   */
  const initContent = useCallback(async () => {
    await Promise.allSettled([getFeaturedArticles(), getAllCategories(), getArticlesByRecent(), getAllContentLabels()]);
  }, [getFeaturedArticles, getAllCategories, getArticlesByRecent, , getAllContentLabels]);

  /**
   * Run calls on startup to get all content and token.
   */
  useEffect(() => {
    if (msalStatus === InteractionStatus.None) {
      initContent();
    }
  }, [initContent, msalStatus, inProgress]);

  return (
    <CMSContext.Provider
      value={{
        latestArticle,
        recentArticles,
        recentArticlesFeaturedCategories,
        categories,
        contentLabels,
        articleDetail,
        featuredArticles,
        homepageCategories,
        isLoadingArticle,
        getArticleById,
        getAuthorById,
        getArticlesByAuthor,
        getArticlesByCategory,
        getDashboardsByAuthor,
        getAllArticles,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
};
